import { ReactNode } from 'react';

// Type-only so this does not create a runtime cycle with the dropdown.
import type { CopilotAction } from 'components/copilotDropdown';

import {
  copilotActionRegistry,
  defaultPinnedState,
} from './copilotActions.registry';
import {
  type CopilotActionContext,
  type CopilotActionId,
  type CopilotStatusArgs,
} from './copilotActions.types';

type CopilotDropdownActionArgs = {
  context: CopilotActionContext;
  /** The panel the dropdown shows for this action. */
  content: ReactNode;
  /** The conversation the action acts on. */
  status?: Pick<CopilotStatusArgs, 'channel' | 'csid'>;
  hasDividerBefore?: boolean;
};

/**
 * Builds a copilot dropdown menu entry from the registry, so title, icon,
 * command, pinned default, visibility and disabled state are not restated per
 * context.
 */
export const copilotDropdownAction = (
  id: CopilotActionId,
  { context, content, status, hasDividerBefore }: CopilotDropdownActionArgs,
): CopilotAction => {
  const entry = copilotActionRegistry[id];
  const name = context.names?.[id] ?? entry.name;
  const { featureFlag, gateDropdownOnStatus, getStatus } = entry;

  return {
    title: name,
    icon: entry.icon,
    commandName: entry.commandName,
    content,
    ...(hasDividerBefore && { hasDividerBefore }),
    isPinned: ({ pinnedState }) => pinnedState?.[id] ?? defaultPinnedState[id],
    ...(featureFlag && {
      isVisible: ({ featureFlags }) => featureFlags?.[featureFlag] ?? false,
    }),
    ...(gateDropdownOnStatus &&
      getStatus && {
        isDisabled: ({ currentEditorText, preconditions }) => {
          const { isDisabled, tooltipContent } = getStatus({
            text: currentEditorText || '',
            name,
            ...status,
            ...preconditions,
          });

          return isDisabled ? tooltipContent : false;
        },
      }),
  };
};
